/**
 * Optimization Results Page - Tabbed view for Optimization Runs, Route Summaries, and Route Steps
 * Features: Modern design, filtering, export capabilities, no edit/delete operations
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Divider,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Chip,
  IconButton,
  Card,
  CardContent,
  LinearProgress,
  Fade,
  Avatar,
  Stack,
  Badge,
  Tooltip,
  ButtonGroup,
  Container,
} from '@mui/material';
import {
  Analytics,
  Assessment,
  Route,
  Timeline,
  Download,
  CheckCircle,
  Schedule,
  Speed,
  CloudDownload,
  FilterList,
  ViewList,
  Refresh,
  TrendingUp,
  Insights,
  DirectionsCar,
  Work,
} from '@mui/icons-material';
import { AdvancedDataTable } from '../components/tables/AdvancedDataTable';
// import BulkExportDialog from '../components/bulk/BulkExportDialog';
import { useVRPData } from '../contexts/VRPDataContext';
import { ProfessionalLayout } from '../components/layout/ProfessionalLayout';
import apiClient from '../services/api';
import { formatDuration } from '../utils/timeFormat';
import type { OptimizationRun } from '../types/vrp';

// Helper function to format time in seconds to AM/PM format
const formatTimeToAMPM = (timeInSeconds: number, isRouteTime: boolean = true): string => {
  // Handle null/undefined/invalid values
  if (timeInSeconds === null || timeInSeconds === undefined || isNaN(timeInSeconds)) {
    return 'N/A';
  }
  
  // Convert to number if it's a string
  const timeValue = Number(timeInSeconds);
  
  // VROOM returns absolute times as seconds since midnight (not relative times)
  // No need to add base offset - use the time value directly
  const absoluteTimeSeconds = timeValue;
  
  const totalMinutes = Math.floor(absoluteTimeSeconds / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  
  // Convert to 12-hour format
  let displayHours = hours % 12;
  if (displayHours === 0) displayHours = 12;
  const ampm = hours < 12 ? 'AM' : 'PM';
  
  // Format with leading zeros
  const formattedTime = `${displayHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}${ampm}`;
  
  return formattedTime;
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`optimization-tabpanel-${index}`}
      aria-labelledby={`optimization-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Fade in={value === index} timeout={300}>
          <Box sx={{ p: 1 }}>
            {children}
          </Box>
        </Fade>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `optimization-tab-${index}`,
    'aria-controls': `optimization-tabpanel-${index}`,
  };
}

// Enhanced theme
const theme = {
  primary: '#1976d2',
  primaryDark: '#1565c0',
  secondary: '#14B8A6',
  accent: '#f59e0b',
  success: '#16a34a',
  warning: '#f59e0b',
  error: '#dc2626',
  background: '#fafafa',
  surface: '#ffffff',
  surfaceVariant: '#f8fafc',
  gradients: {
    primary: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
    secondary: 'linear-gradient(135deg, #14B8A6 0%, #0f766e 100%)',
    accent: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    success: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
  },
  shadows: {
    card: '0 4px 20px rgba(0, 0, 0, 0.08)',
    elevated: '0 8px 32px rgba(0, 0, 0, 0.12)',
    float: '0 12px 40px rgba(0, 0, 0, 0.15)',
  },
};

// Optimization Runs columns
const OPTIMIZATION_RUNS_COLUMNS = [
  {
    id: 'id',
    label: 'Run ID',
    dataKey: 'id',
    sortable: true,
    filterable: true,
    width: 80,
    align: 'center' as const,
  },
  {
    id: 'timestamp',
    label: 'Timestamp',
    dataKey: 'timestamp',
    sortable: true,
    filterable: true,
    width: 180,
    renderCell: (run: any) => (
      <Box>
        <Typography variant="body2" fontWeight="600">
          {new Date(run.timestamp).toLocaleDateString()}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {new Date(run.timestamp).toLocaleTimeString()}
        </Typography>
      </Box>
    ),
  },
  {
    id: 'status',
    label: 'Status',
    dataKey: 'status',
    sortable: true,
    filterable: true,
    width: 120,
    renderCell: (run: any) => (
      <Chip
        label={run.status}
        color={run.status === 'completed' ? 'success' : run.status === 'error' ? 'error' : 'default'}
        size="small"
        icon={run.status === 'completed' ? <CheckCircle /> : undefined}
      />
    ),
  },
  {
    id: 'total_vehicles',
    label: 'Vehicle Count',
    dataKey: 'total_vehicles',
    sortable: true,
    filterable: true,
    width: 120,
    align: 'center' as const,
    renderCell: (run: OptimizationRun) => (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
        <Avatar sx={{ 
          width: 24, 
          height: 24, 
          bgcolor: theme.primary, 
          fontSize: '0.75rem',
          '@media (max-width: 768px)': {
            width: 20,
            height: 20,
            fontSize: '0.6rem'
          }
        }}>
          <DirectionsCar fontSize="small" />
        </Avatar>
        <Typography 
          variant="body2" 
          fontWeight="600" 
          sx={{
            color: theme.primary,
            '@media (max-width: 768px)': {
              fontSize: '0.75rem'
            }
          }}
        >
          {run.total_vehicles || 0}
        </Typography>
      </Box>
    ),
  },
  {
    id: 'total_jobs',
    label: 'Jobs',
    dataKey: 'total_jobs',
    sortable: true,
    filterable: true,
    width: 100,
    align: 'center' as const,
    renderCell: (run: OptimizationRun) => (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
        <Avatar sx={{
          bgcolor: `${theme.secondary}20`,
          color: theme.secondary,
          width: 24,
          height: 24,
          fontSize: '0.75rem'
        }}>
          <Work fontSize="small" />
        </Avatar>
        <Typography 
          variant="body2" 
          fontWeight="600" 
          sx={{
            color: theme.secondary,
            '@media (max-width: 768px)': {
              fontSize: '0.75rem'
            }
          }}
        >
          {run.total_jobs || 0}
        </Typography>
      </Box>
    ),
  },
  {
    id: 'total_routes',
    label: 'Routes',
    dataKey: 'total_routes',
    sortable: true,
    width: 100,
    align: 'center' as const,
  },
  {
    id: 'total_unassigned',
    label: 'Unassigned',
    dataKey: 'total_unassigned',
    sortable: true,
    width: 110,
    align: 'center' as const,
    renderCell: (run: any) => (
      <Chip
        label={run.total_unassigned}
        color={run.total_unassigned > 0 ? 'warning' : 'success'}
        size="small"
      />
    ),
  },
  {
    id: 'total_distance',
    label: 'Distance (km)',
    dataKey: 'total_distance',
    sortable: true,
    width: 120,
    align: 'right' as const,
    renderCell: (run: any) => (
      <Typography variant="body2">
        {run.total_distance_km || '0.00'}
      </Typography>
    ),
  },
  {
    id: 'total_duration',
    label: 'Duration (min)',
    dataKey: 'total_duration',
    sortable: true,
    width: 120,
    renderCell: (run: any) => (
      <Typography variant="body2">
        {run.total_duration_minutes || 0}
      </Typography>
    ),
  },
  {
    id: 'total_cost',
    label: 'Total Cost',
    dataKey: 'total_cost',
    sortable: true,
    width: 120,
    align: 'right' as const,
    renderCell: (run: any) => (
      <Typography variant="body2" fontWeight="600">
        {run.total_cost?.toLocaleString() || 0}
      </Typography>
    ),
  },
  {
    id: 'computing_time',
    label: 'Compute Time (s)',
    dataKey: 'computing_time',
    sortable: true,
    width: 120,
    renderCell: (run: any) => (
      <Typography variant="body2">
        {(run.computing_time / 1000).toFixed(2)}
      </Typography>
    ),
  },
  {
    id: 'dataset_name',
    label: 'Dataset',
    dataKey: 'dataset_name',
    sortable: true,
    filterable: true,
    width: 180,
    renderCell: (run: any) => (
      <Box>
        <Typography variant="body2" fontWeight="600">
          {run.dataset_name || 'N/A'}
        </Typography>
        {run.dataset_version && (
          <Typography variant="caption" color="text.secondary">
            v{run.dataset_version}
          </Typography>
        )}
      </Box>
    ),
  },
];

// Route Summaries columns
const ROUTE_SUMMARIES_COLUMNS = [
  {
    id: 'id',
    label: 'Route ID',
    dataKey: 'id',
    sortable: true,
    filterable: true,
    width: 80,
    align: 'center' as const,
  },
  {
    id: 'optimization_run_id',
    label: 'Run ID',
    dataKey: 'optimization_run_id',
    sortable: true,
    filterable: true,
    width: 80,
    align: 'center' as const,
  },
  {
    id: 'vehicle_id',
    label: 'Vehicle',
    dataKey: 'vehicle_id',
    sortable: true,
    filterable: true,
    width: 100,
    renderCell: (route: any) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Avatar sx={{ width: 24, height: 24, bgcolor: theme.primary, fontSize: '0.75rem' }}>
          <DirectionsCar fontSize="small" />
        </Avatar>
        <Typography variant="body2">
          {route.vehicle_id}
        </Typography>
      </Box>
    ),
  },
  {
    id: 'cost',
    label: 'Cost',
    dataKey: 'cost',
    sortable: true,
    width: 100,
    align: 'right' as const,
    renderCell: (route: any) => (
      <Typography variant="body2" fontWeight="600">
        {route.cost?.toLocaleString() || 0}
      </Typography>
    ),
  },
  {
    id: 'distance',
    label: 'Distance (km)',
    dataKey: 'distance',
    sortable: true,
    width: 120,
    align: 'right' as const,
    renderCell: (route: any) => (
      <Typography variant="body2">
        {route.distance_km || '0.00'}
      </Typography>
    ),
  },
  {
    id: 'total_trip_time',
    label: 'Total Trip Time',
    dataKey: 'total_trip_time',
    sortable: true,
    width: 140,
    renderCell: (route: any) => {
      // Calculate total trip time: drive time + setup time + service time + wait time
      const driveTime = route.duration || 0;
      const setupTime = route.setup_time || 0;
      const serviceTime = route.service_time || 0;
      const waitTime = route.waiting_time || 0;
      const totalTripTime = driveTime + setupTime + serviceTime + waitTime;
      
      return (
        <Typography variant="body2">
          {formatDuration(totalTripTime)}
        </Typography>
      );
    },
  },
  {
    id: 'duration',
    label: 'Drive Time (min)',
    dataKey: 'duration',
    sortable: true,
    width: 120,
    renderCell: (route: any) => (
      <Typography variant="body2">
        {formatDuration(route.duration || 0)}
      </Typography>
    ),
  },
  {
    id: 'service_time',
    label: 'Service Time (min)',
    dataKey: 'service_time',
    sortable: true,
    width: 120,
    renderCell: (route: any) => (
      <Typography variant="body2">
        {formatDuration(route.service_time || 0)}
      </Typography>
    ),
  },
  {
    id: 'waiting_time',
    label: 'Waiting Time (min)',
    dataKey: 'waiting_time',
    sortable: true,
    width: 120,
    renderCell: (route: any) => (
      <Typography variant="body2">
        {route.waiting_time_minutes || 0}
      </Typography>
    ),
  },
  {
    id: 'setup_time',
    label: 'Setup Time (min)',
    dataKey: 'setup_time',
    sortable: true,
    width: 120,
    renderCell: (route: any) => (
      <Typography variant="body2">
        {Math.floor(route.setup_time / 60)}
      </Typography>
    ),
  },
  {
    id: 'deliveries',
    label: 'Deliveries',
    dataKey: 'deliveries',
    sortable: false,
    width: 120,
    renderCell: (route: any) => (
      <Typography variant="body2">
        {route.deliveries ? JSON.stringify(route.deliveries) : 'N/A'}
      </Typography>
    ),
  },
  {
    id: 'pickups',
    label: 'Pickups',
    dataKey: 'pickups',
    sortable: false,
    width: 120,
    renderCell: (route: any) => (
      <Typography variant="body2">
        {route.pickups ? JSON.stringify(route.pickups) : 'N/A'}
      </Typography>
    ),
  },
  {
    id: 'delivery_count',
    label: 'NUMBER OF DEL.',
    dataKey: 'delivery_count',
    sortable: true,
    filterable: true,
    width: 130,
    align: 'center' as const,
    renderCell: (route: any) => (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
        <Avatar sx={{ 
          width: 24, 
          height: 24, 
          bgcolor: theme.accent, 
          fontSize: '0.75rem',
          '@media (max-width: 768px)': {
            width: 20,
            height: 20,
            fontSize: '0.6rem'
          }
        }}>
          <Work fontSize="small" />
        </Avatar>
        <Typography 
          variant="body2" 
          fontWeight="600" 
          sx={{
            color: theme.accent,
            '@media (max-width: 768px)': {
              fontSize: '0.75rem'
            }
          }}
        >
          {route.delivery_count || 0}
        </Typography>
      </Box>
    ),
  },
];

// Route Steps columns
const ROUTE_STEPS_COLUMNS = [
  {
    id: 'optimization_run_id',
    label: 'Run ID',
    dataKey: 'optimization_run_id',
    sortable: true,
    filterable: true,
    width: 80,
    align: 'center' as const,
    format: (value: any) => value || 'N/A',
  },
  {
    id: 'id',
    label: 'Step ID',
    dataKey: 'id',
    sortable: true,
    filterable: true,
    width: 80,
    align: 'center' as const,
  },
  {
    id: 'route_summary_id',
    label: 'Route ID',
    dataKey: 'route_summary_id',
    sortable: true,
    filterable: true,
    width: 80,
    align: 'center' as const,
  },
  {
    id: 'vehicle_id',
    label: 'Vehicle',
    dataKey: 'vehicle_id',
    sortable: true,
    filterable: true,
    width: 100,
    renderCell: (step: any) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Avatar sx={{ width: 24, height: 24, bgcolor: theme.primary, fontSize: '0.75rem' }}>
          <DirectionsCar fontSize="small" />
        </Avatar>
        <Typography variant="body2">
          {step.vehicle_id}
        </Typography>
      </Box>
    ),
  },
  {
    id: 'step_type',
    label: 'Step Type',
    dataKey: 'step_type',
    sortable: true,
    filterable: true,
    width: 120,
    renderCell: (step: any) => (
      <Chip
        label={step.step_type}
        size="small"
        color={
          step.step_type === 'start' ? 'success' :
          step.step_type === 'end' ? 'error' :
          step.step_type === 'job' ? 'primary' : 'default'
        }
        icon={
          step.step_type === 'job' ? <Work /> :
          step.step_type === 'start' || step.step_type === 'end' ? <DirectionsCar /> : undefined
        }
      />
    ),
  },
  {
    id: 'step_order',
    label: 'Order',
    dataKey: 'step_order',
    sortable: true,
    width: 80,
    align: 'center' as const,
  },
  {
    id: 'job_id',
    label: 'Job ID',
    dataKey: 'job_id',
    sortable: true,
    filterable: true,
    width: 80,
    align: 'center' as const,
    renderCell: (step: any) => (
      <Typography variant="body2">
        {step.job_id || 'N/A'}
      </Typography>
    ),
  },
  {
    id: 'location',
    label: 'Location ID',
    dataKey: 'location_id',
    sortable: true,
    filterable: true,
    width: 120,
    align: 'center' as const,
    renderCell: (step: any) => (
      <Typography variant="body2">
        {step.location_id || 'N/A'}
      </Typography>
    ),
  },
  {
    id: 'arrival_time',
    label: 'Arrival Time',
    dataKey: 'arrival_time_formatted',
    sortable: true,
    width: 120,
    renderCell: (step: any) => (
      <Typography variant="body2">
        {step.arrival_time_formatted || 'N/A'}
      </Typography>
    ),
  },
  {
    id: 'departure_time',
    label: 'Departure Time',
    dataKey: 'departure_time_formatted',
    sortable: false,
    width: 120,
    renderCell: (step: any) => {
      // For start steps, show dash
      if (step.step_type === 'start') {
        return (
          <Typography variant="body2" color="text.secondary">
            —
          </Typography>
        );
      }
      
      // Use pre-formatted departure time from Edge Function
      return (
        <Typography variant="body2" color={step.step_type === 'end' ? 'text.secondary' : 'text.primary'}>
          {step.departure_time_formatted || 'N/A'}
        </Typography>
      );
    },
  },
  {
    id: 'setup_time',
    label: 'Setup (min)',
    dataKey: 'setup_time_minutes',
    sortable: true,
    width: 100,
    renderCell: (step: any) => (
      <Typography variant="body2">
        {step.setup_time_minutes || 0}
      </Typography>
    ),
  },
  {
    id: 'service_time',
    label: 'Service (min)',
    dataKey: 'service_time_minutes',
    sortable: true,
    width: 100,
    renderCell: (step: any) => (
      <Typography variant="body2">
        {step.service_time_minutes || 0}
      </Typography>
    ),
  },
  {
    id: 'waiting_time',
    label: 'Waiting (min)',
    dataKey: 'waiting_time_minutes',
    sortable: true,
    width: 100,
    renderCell: (step: any) => (
      <Typography variant="body2">
        {step.waiting_time_minutes || 0}
      </Typography>
    ),
  },
  {
    id: 'distance',
    label: 'Distance (km)',
    dataKey: 'distance_km',
    sortable: true,
    width: 120,
    align: 'right' as const,
    renderCell: (step: any) => (
      <Typography variant="body2" align="right">
        {step.distance_km || '0.00'}
      </Typography>
    ),
  },
  {
    id: 'duration',
    label: 'Duration (min)',
    dataKey: 'duration_minutes',
    sortable: true,
    width: 120,
    renderCell: (step: any) => (
      <Typography variant="body2">
        {formatDuration(step.duration || 0)}
      </Typography>
    ),
  },
];

export const OptimizationResults: React.FC = () => {
  // TODO: Replace with Toolpad navigation
  const currentProject = null;
  const { currentDataset } = useVRPData(); // Get dataset context
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Tab state - initialize from URL if available
  const [tabValue, setTabValue] = useState(() => {
    const tab = new URLSearchParams(window.location.search).get('tab');
    const tabIndex = ['runs', 'summaries', 'steps'].indexOf(tab || '');
    console.log('🔍 Initial tab state from URL:', { tab, tabIndex });
    return tabIndex !== -1 ? tabIndex : 0;
  });

  // Data states
  const [optimizationRuns, setOptimizationRuns] = useState<OptimizationRun[]>([]);
  const [routeSummaries, setRouteSummaries] = useState<any[]>([]);
  const [routeSteps, setRouteSteps] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Export dialog state
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportType, setExportType] = useState<'optimization_runs' | 'route_summaries' | 'route_steps'>('optimization_runs');

  // Snackbar state
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  // Initialize tab from URL params
  useEffect(() => {
    const tab = searchParams.get('tab');
    console.log('🔍 URL tab parameter:', tab, 'current tabValue:', tabValue);
    if (tab) {
      const tabIndex = ['runs', 'summaries', 'steps'].indexOf(tab);
      console.log('🔍 Tab index calculated:', tabIndex, 'for tab:', tab);
      if (tabIndex !== -1 && tabIndex !== tabValue) {
        console.log('✅ Setting tab value to:', tabIndex);
        setTabValue(tabIndex);
      } else if (tabIndex === tabValue) {
        console.log('🔄 Tab value already correct:', tabIndex);
      } else {
        console.log('❌ Invalid tab name:', tab);
      }
    } else {
      console.log('🔍 No tab parameter in URL, current tabValue:', tabValue);
      // If we have a non-zero tab value but no URL param, preserve the tab and update URL
      if (tabValue !== 0) {
        const tabs = ['runs', 'summaries', 'steps'];
        console.log('🔧 Restoring URL parameter for tab:', tabs[tabValue]);
        setSearchParams({ tab: tabs[tabValue] });
      }
    }
  }, [searchParams, tabValue]);

  // Defensive tab value validation to prevent React error #185
  const safeTabValue = React.useMemo(() => {
    if (tabValue === null || tabValue === undefined) {
      console.warn('🔧 [OptimizationResults] tabValue is null/undefined, defaulting to 0');
      return 0;
    }
    
    const numericValue = Number(tabValue);
    if (isNaN(numericValue) || numericValue < 0 || numericValue > 2 || !Number.isInteger(numericValue)) {
      console.warn('🔧 [OptimizationResults] Invalid tabValue detected, resetting to 0:', tabValue, 'parsed:', numericValue);
      return 0;
    }
    return numericValue;
  }, [tabValue]);

  // Handle tab change with defensive validation
  const handleTabChange = React.useCallback((event: React.SyntheticEvent, newValue: number) => {
    // Defensive validation to prevent React error #185
    if (!event || event === null) {
      console.warn('⚠️ [OptimizationResults] Invalid event in handleTabChange:', event);
      return;
    }
    
    // Ensure newValue is a valid number and within range
    const validatedValue = Number(newValue);
    if (isNaN(validatedValue) || validatedValue < 0 || validatedValue > 2) {
      console.warn('⚠️ [OptimizationResults] Invalid tab value, keeping current:', newValue, 'current:', tabValue);
      return;
    }
    
    console.log('🔄 [OptimizationResults] Tab change:', { from: tabValue, to: validatedValue });
    
    // Only update if the value actually changed to prevent unnecessary re-renders
    if (validatedValue !== tabValue) {
      setTabValue(validatedValue);
      const tabs = ['runs', 'summaries', 'steps'];
      setSearchParams({ tab: tabs[validatedValue] });
    }
  }, [tabValue, setSearchParams]);

  // Fetch data based on current tab
  useEffect(() => {
    console.log('🔍 OptimizationResults useEffect triggered:', {
      hasCurrentProject: !!currentProject?.id,
      currentProjectId: currentProject?.id,
      tabValue: safeTabValue,
      tabName: ['runs', 'summaries', 'steps'][safeTabValue]
    });
    
    if (!currentProject?.id) {
      console.log('❌ No current project, skipping data fetch');
      return;
    }

    const fetchData = async () => {
      console.log(`🚀 Starting data fetch for tab ${safeTabValue} (${['runs', 'summaries', 'steps'][safeTabValue]})`);
      setLoading(true);
      setError(null);

      try {
        switch (safeTabValue) {
          case 0: // Optimization Runs
            console.log('📊 Fetching Optimization Runs...');
            const runs = await apiClient.getOptimizationRuns(currentProject.id, { limit: 100 });
            console.log('🔍 OptimizationResults: Optimization runs fetched:', runs);
            console.log('📊 OptimizationResults: Sample run structure:', runs?.[0]);
            if (runs?.[0]?.raw_response) {
              console.log('🔍 OptimizationResults: Sample raw_response keys:', Object.keys(runs[0].raw_response));
            }
            setOptimizationRuns(runs || []);
            break;
          case 1: // Route Summaries
            console.log('🛣️ Fetching Route Summaries...');
            const routes = await apiClient.getRouteSummaries(currentProject.id);
            setRouteSummaries(routes || []);
            break;
          case 2: // Route Steps
            console.log('📍 Fetching Route Steps...');
            const steps = await apiClient.getRouteSteps(currentProject.id);
            console.log('🔍 OptimizationResults: Route steps fetched:', steps);
            console.log('📊 OptimizationResults: Steps count:', steps?.length || 0);
            
            // Debug route steps data structure
            if (steps && steps.length > 0) {
              console.log('✅ Route steps loaded successfully:', steps.length);
              console.log('📋 Sample step with formatted data:', steps[0]);
            }
            
            setRouteSteps(steps || []);
            break;
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch data';
        setError(errorMessage);
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentProject?.id, tabValue]);

  // Get current data and columns based on tab
  const getCurrentData = () => {
    switch (safeTabValue) {
      case 0: return optimizationRuns;
      case 1: return routeSummaries;
      case 2: return routeSteps;
      default: return [];
    }
  };

  const getCurrentColumns = () => {
    switch (safeTabValue) {
      case 0: return OPTIMIZATION_RUNS_COLUMNS;
      case 1: return ROUTE_SUMMARIES_COLUMNS;
      case 2: return ROUTE_STEPS_COLUMNS;
      default: return [];
    }
  };

  const getCurrentExportType = () => {
    switch (safeTabValue) {
      case 0: return 'optimization_runs';
      case 1: return 'route_summaries';
      case 2: return 'route_steps';
      default: return 'optimization_runs';
    }
  };

  // Handle export
  const handleExport = () => {
    setExportType(getCurrentExportType() as any);
    setExportDialogOpen(true);
  };


  // Handle refresh
  const handleRefresh = () => {
    // Trigger data fetch by updating a state or calling fetch directly
    const fetchData = async () => {
      if (!currentProject?.id) return;

      setLoading(true);
      setError(null);

      try {
        switch (safeTabValue) {
          case 0:
            const runs = await apiClient.getOptimizationRuns(currentProject.id, { limit: 100 });
            setOptimizationRuns(runs || []);
            break;
          case 1:
            const routes = await apiClient.getRouteSummaries(currentProject.id);
            setRouteSummaries(routes || []);
            break;
          case 2:
            const steps = await apiClient.getRouteSteps(currentProject.id);
            setRouteSteps(steps || []);
            break;
        }
        setSnackbar({
          open: true,
          message: 'Data refreshed successfully',
          severity: 'success',
        });
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to refresh data';
        setError(errorMessage);
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  };

  return (
    <ProfessionalLayout 
      pageTitle="Optimization Results" 
      pageSubtitle="Analysis and export of optimization runs, route summaries, and detailed step data"
      activeTab="Optimization"
      actionButtons={
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={handleRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={handleExport}
            disabled={loading || getCurrentData().length === 0}
          >
            Export
          </Button>
        </Stack>
      }
    >
      <Container maxWidth={false} sx={{ py: 0 }}>
        {/* Status Messages */}
        {currentProject && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Project:</strong> {currentProject.name} (ID: {currentProject.id})
            </Typography>
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

      {/* Main Content */}
      <Paper 
        elevation={0} 
        sx={{ 
          borderRadius: 3,
          overflow: 'hidden',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          boxShadow: theme.shadows.card,
          border: '1px solid rgba(0, 0, 0, 0.05)',
        }}
      >
        {/* Tabs */}
        <Box sx={{ 
          borderBottom: 1, 
          borderColor: 'divider',
          background: 'linear-gradient(90deg, #f8fafc 0%, #ffffff 50%, #f8fafc 100%)',
        }}>
          <Tabs
            value={safeTabValue}
            onChange={handleTabChange}
            aria-label="optimization results tabs"
            variant="fullWidth"
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '1rem',
                minHeight: 72,
                '&.Mui-selected': {
                  background: theme.gradients.primary,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                },
              },
            }}
          >
            <Tab
              icon={<Analytics />}
              label="Optimization Runs"
              value={0}
              {...a11yProps(0)}
            />
            <Tab
              icon={<Route />}
              label="Route Summaries"
              value={1}
              {...a11yProps(1)}
            />
            <Tab
              icon={<Timeline />}
              label="Route Steps"
              value={2}
              {...a11yProps(2)}
            />
          </Tabs>
        </Box>

        {/* Tab Panels */}
        <Box sx={{ minHeight: 600 }}>
          {loading && <LinearProgress />}
          
          <TabPanel value={safeTabValue} index={0}>
            <AdvancedDataTable
              data={optimizationRuns}
              columns={OPTIMIZATION_RUNS_COLUMNS}
              loading={loading}
              keyField="id"
              enableSelection={false}
              enableFiltering={true}
              title="Optimization Runs"
              onRowClick={(run) => {
                console.log('Optimization run clicked:', run);
              }}
            />
          </TabPanel>

          <TabPanel value={safeTabValue} index={1}>
            <AdvancedDataTable
              data={routeSummaries}
              columns={ROUTE_SUMMARIES_COLUMNS}
              loading={loading}
              keyField="id"
              enableSelection={false}
              enableFiltering={true}
              title="Route Summaries"
              onRowClick={(route) => {
                console.log('Route summary clicked:', route);
              }}
            />
          </TabPanel>

          <TabPanel value={safeTabValue} index={2}>
            {console.log('🔍 Rendering Route Steps TabPanel:', {
              dataCount: routeSteps.length,
              columnsCount: ROUTE_STEPS_COLUMNS.length,
              loading,
              firstStepData: routeSteps[0],
              firstColumnId: ROUTE_STEPS_COLUMNS[0]?.id,
              filterableColumns: ROUTE_STEPS_COLUMNS.filter(col => col.filterable).map(col => ({ id: col.id, label: col.label }))
            })}
            <AdvancedDataTable
              data={routeSteps}
              columns={ROUTE_STEPS_COLUMNS}
              loading={loading}
              keyField="id"
              enableSelection={false}
              enableFiltering={true}
              title="Route Steps"
              onRowClick={(step) => {
                console.log('Route step clicked:', step);
              }}
            />
          </TabPanel>
        </Box>
      </Paper>

      {/* Export Dialog */}
      {/* <BulkExportDialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        entityType={getCurrentExportType() as any}
        data={getCurrentData()}
        projectName={currentProject?.name || 'project'}
      /> */}

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          message={snackbar.message}
        />
      </Container>
    </ProfessionalLayout>
  );
};